/* ── Lead origin: label, pill style and filter ─────────────────────────
   How a lead's `origin.source` (set once, by the backend, when the lead is
   created) is shown on the Leads page and matched by its Origin filter.

   Kept out of LeadList so it can be tested without rendering the page — a
   mismatch here is invisible in the UI: the lead is still listed, it just
   shows "—" and vanishes from its own filter.

   Every value CustomerOrigin.source can hold (backend real_estate.py):
     platform · share_page · share_otp · direct_otp · guided_room · embed · sdk

   `embed` is the live value for a lead captured through a walkthrough on a
   builder's own website. `sdk` is reserved on the backend ("defined for
   later; not populated yet") and was, until 2026-09-17, the ONLY one this page
   understood — so every real embed lead showed "—" and the Embed filter came
   back empty. Both now read "Embed", so it keeps working whichever one the
   backend settles on. */

export interface OriginMeta {
  label: string;
  cls: string;
  /** Identified by OTP — the pill gets a verified tick. */
  otp: boolean;
}

/* The labels match the pills in originMeta — a filter that says "Guided Tour"
   while the column says something else is worse than no filter.

   `share` and `embed` are filter buckets rather than raw enum values:
     share = share_page + share_otp  (a lead off a shared link, with or without OTP)
     embed = embed + sdk             (see the header note) */
export const ORIGIN_OPTIONS = [
  { value: "", label: "All Origins" },
  { value: "platform", label: "Platform" },
  { value: "share", label: "Share Page" },
  { value: "guided_room", label: "Guided Tour" },
  { value: "embed", label: "Embed" },
];

const EMBED_SOURCES = new Set(["embed", "sdk"]);

export const isEmbedOrigin = (source?: string): boolean =>
  EMBED_SOURCES.has(String(source || ""));

export const originMeta = (source?: string): OriginMeta => {
  if (isEmbedOrigin(source)) {
    /* "Embed" is what this is called everywhere in the product — the snippet a
       builder pastes into their own site. Same pill style it always had; only
       which values reach it changed. */
    return { label: "Embed", cls: "bg-white/5 text-[#C7D6DD]", otp: true };
  }
  switch (source) {
    case "platform":
      return { label: "Platform", cls: "bg-[#0e3a2e] text-[#34D399]", otp: false };
    case "share_page":
      return { label: "Share Page", cls: "bg-[#13314d] text-[#7DB6FB]", otp: false };
    case "share_otp":
      return { label: "Share Page", cls: "bg-[#13314d] text-[#7DB6FB]", otp: true };
    case "direct_otp":
      return { label: "Direct", cls: "bg-[#3a2f13] text-[#E3B341]", otp: false };
    case "guided_room":
      return { label: "Guided Tour", cls: "bg-white/5 text-[#C7D6DD]", otp: false };
    default:
      return { label: "—", cls: "bg-transparent text-[#7C97A8]", otp: false };
  }
};

/** Does a lead with this origin belong under the chosen Origin filter? */
export const matchesOriginFilter = (source: string | undefined, filter: string): boolean => {
  if (!filter) return true;
  if (filter === "share") return source === "share_page" || source === "share_otp";
  if (filter === "embed") return isEmbedOrigin(source);
  return source === filter;
};
